import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useExpenses } from '../../../hooks/useExpenses';
import { useUserData } from '../../../hooks/useUserData';
import { buildApiUrl } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useDemo } from '../../../contexts/DemoContext';

// Vega-Lite diverging heatmap: Years on X, single band on Y, color = YoY change (%)
const SurplusDeficitHeatmap = () => {
  const { expenses, loading } = useExpenses();
  const { userData } = useUserData();
  const { getAuthHeaders } = useAuth();
  const { getDemoQueryParams } = useDemo();
  const containerRef = useRef(null);

  const startYear = useMemo(() => Number(userData?.setup?.startYear) || new Date().getFullYear(), [userData]);
  const horizon = useMemo(() => Math.max(1, Math.min(60, Number(userData?.setup?.projectionHorizon) || 20)), [userData]);

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchRows = async () => {
      try {
        const queryParams = getDemoQueryParams();
        const res = await fetch(buildApiUrl(`/api/dashboard/expense-heatmap${queryParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load heatmap');
        const json = await res.json();
        if (json?.success && Array.isArray(json.data)) {
          setRows(json.data);
          const cats = Array.from(new Set(json.data.map((r) => r.category))).sort();
          setCategories(cats);
        } else {
          setRows([]);
          setCategories([]);
        }
      } catch (e) {
        console.error(e);
        setRows([]);
        setCategories([]);
      }
    };
    fetchRows();
  }, [userData, getDemoQueryParams, getAuthHeaders]);

  useEffect(() => {
    if (loading) return;
    const embedChart = async () => {
      // Load vega libs from CDN once
      const ensureScript = (src) => new Promise((resolve) => {
        const existing = Array.from(document.getElementsByTagName('script')).find(s => s.src === src);
        if (existing) return existing.addEventListener('load', () => resolve());
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });

      if (!window.vegaEmbed) {
        await ensureScript('https://cdn.jsdelivr.net/npm/vega@5');
        await ensureScript('https://cdn.jsdelivr.net/npm/vega-lite@5');
        await ensureScript('https://cdn.jsdelivr.net/npm/vega-embed@6');
      }

      const spec = {
        $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
        data: { values: rows },
        mark: { type: 'rect', tooltip: true },
        encoding: {
          x: { field: 'year', type: 'ordinal', title: 'Year' },
          y: { field: 'category', type: 'nominal', title: 'Expense Type', sort: categories },
          color: {
            field: 'total', type: 'quantitative', title: 'Total (₹)',
            scale: { scheme: 'reds' }
          },
          tooltip: [
            { field: 'year', type: 'ordinal', title: 'Year' },
            { field: 'category', type: 'nominal', title: 'Type' },
            { field: 'total', type: 'quantitative', title: 'Total (₹)', format: ',' },
            { field: 'yoy', type: 'quantitative', title: 'YoY (%)', format: '.1f' }
          ]
        },
        width: 'container',
        height: Math.max(100, categories.length * 24),
        config: { view: { stroke: null } }
      };

      try {
        if (containerRef.current && window.vegaEmbed) {
          await window.vegaEmbed(containerRef.current, spec, { actions: false });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Vega embed error:', e);
      }
    };

    embedChart();
  }, [rows, loading, categories]);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground">Expense Heatmap by Type</h3>
        <p className="text-sm text-muted-foreground">Years on X, types on Y, color = Total (₹), higher → red</p>
      </div>
      <div ref={containerRef} className="w-full" />
    </div>
  );
};

export default SurplusDeficitHeatmap;